/**
 * AutoGroupLightsModal
 *
 * 조명 자동 그룹핑 미리보기 모달. 임계값(거리) 슬라이더를 조정하면 즉시 클러스터
 * 수가 갱신된다. "진행" 클릭 시 호출자가 cluster id 배열들로 실제 그룹을 만든다.
 *
 * 표시 정보:
 *  - 후보 조명 셀 개수 (orphan 라이트 + 기존 라이트 프레임 그룹 둘 다)
 *  - 생성 예정 그룹 수 / 흡수될 기존 그룹 수
 *  - 그룹에 포함될 셀 총합
 *  - 임계값 슬라이더 (THRESHOLD_MIN ~ THRESHOLD_MAX)
 *    · 추천 기본값: medianShortSide × 0.5
 *    · 도면 좌표 단위가 다양한 프로젝트를 한 모달로 지원하기 위해 고정 범위 사용
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './RelationTypeManager.module.css'
import type { AutoGroupCandidate } from '../hooks/useAutoGroupLights'

/** 임계값 입력 허용 범위 — 도면 좌표 단위가 mm/m/normalized 등 다양해 0.001~100 으로 폭넓게 허용. */
const THRESHOLD_MIN = 0.001
const THRESHOLD_MAX = 100
const THRESHOLD_STEP = 0.001

const clampThreshold = (v: number): number =>
  Math.min(THRESHOLD_MAX, Math.max(THRESHOLD_MIN, v))

interface AutoGroupLightsModalProps {
  candidates: AutoGroupCandidate[]
  /** 추천 임계값 (median short side × 0.5). 슬라이더 초기값. [THRESHOLD_MIN, THRESHOLD_MAX] 으로 clamp. */
  suggestedThreshold: number
  /** 추천값 안내 문구에만 사용 — 실제 슬라이더 범위는 THRESHOLD_MIN/MAX 고정. */
  medianShortSide: number
  /**
   * 주어진 임계값으로 클러스터링한 결과를 즉시 반환. modal 은 슬라이더가 움직일 때
   * 이 함수를 호출해 카운트만 갱신한다.
   */
  clusterFn: (
    candidates: AutoGroupCandidate[],
    threshold: number,
  ) => string[][]
  onConfirm: (clusters: string[][], thresholdUsed: number) => void
  onCancel: () => void
}

export function AutoGroupLightsModal({
  candidates,
  suggestedThreshold,
  medianShortSide,
  clusterFn,
  onConfirm,
  onCancel,
}: AutoGroupLightsModalProps) {
  const [threshold, setThreshold] = useState<number>(
    clampThreshold(suggestedThreshold),
  )

  // 모달 드래그 이동 — 헤더를 잡고 끌어서 자유 배치.
  // 화면 중앙 고정이라 도면을 가렸을 때 비교가 어려워서 사용자 요청으로 추가.
  const [drag, setDrag] = useState<{ dx: number; dy: number }>({ dx: 0, dy: 0 })
  const dragStartRef = useRef<{
    pointerX: number
    pointerY: number
    originDx: number
    originDy: number
  } | null>(null)

  const handleHeaderPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // 닫기 버튼 등 헤더 내 다른 인터랙티브 요소 클릭은 드래그로 전환하지 않는다.
      if ((e.target as HTMLElement).closest('button')) return
      e.preventDefault()
      dragStartRef.current = {
        pointerX: e.clientX,
        pointerY: e.clientY,
        originDx: drag.dx,
        originDy: drag.dy,
      }
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    },
    [drag.dx, drag.dy],
  )

  const handleHeaderPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = dragStartRef.current
      if (!start) return
      setDrag({
        dx: start.originDx + (e.clientX - start.pointerX),
        dy: start.originDy + (e.clientY - start.pointerY),
      })
    },
    [],
  )

  const handleHeaderPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      dragStartRef.current = null
      try {
        ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
      } catch {
        // pointer 이미 해제된 경우 무시
      }
    },
    [],
  )

  // 윈도우 리사이즈 시 모달이 화면 밖으로 사라지지 않도록 재중앙화.
  useEffect(() => {
    const onResize = () => setDrag({ dx: 0, dy: 0 })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // 임계값 바뀔 때마다 재클러스터링. n 이 수백이면 O(n²) 도 millisecond.
  const clusters = useMemo(
    () => clusterFn(candidates, threshold),
    [candidates, threshold, clusterFn],
  )

  // orphan vs 기존 그룹 분리 카운트 — 어떤 종류가 흡수되는지 보여준다.
  const existingGroupIds = useMemo(() => {
    const s = new Set<string>()
    for (const c of candidates) if (c.isExistingGroup) s.add(c.id)
    return s
  }, [candidates])

  const stats = useMemo(() => {
    let absorbedGroups = 0
    let absorbedOrphans = 0
    let newGroupClusters = 0
    let mergeClusters = 0
    for (const cluster of clusters) {
      let groupHits = 0
      for (const id of cluster) {
        if (existingGroupIds.has(id)) groupHits += 1
      }
      if (groupHits === 0) {
        newGroupClusters += 1
        absorbedOrphans += cluster.length
      } else {
        mergeClusters += 1
        absorbedGroups += groupHits
        absorbedOrphans += cluster.length - groupHits
      }
    }
    const totalCells = absorbedOrphans + absorbedGroups
    return {
      newGroupClusters,
      mergeClusters,
      absorbedOrphans,
      absorbedGroups,
      totalCells,
    }
  }, [clusters, existingGroupIds])

  const totalCellsInClusters = stats.totalCells
  const orphanCount = candidates.length - totalCellsInClusters

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel()
    if (e.key === 'Enter') onConfirm(clusters, threshold)
  }

  const canProceed = clusters.length > 0

  return (
    <div className={styles.overlay} onClick={onCancel} onKeyDown={handleKeyDown}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: `translate(${drag.dx}px, ${drag.dy}px)`,
        }}
      >
        <div
          className={styles.header}
          onPointerDown={handleHeaderPointerDown}
          onPointerMove={handleHeaderPointerMove}
          onPointerUp={handleHeaderPointerUp}
          onPointerCancel={handleHeaderPointerUp}
          style={{
            cursor: dragStartRef.current ? 'grabbing' : 'grab',
            userSelect: 'none',
            touchAction: 'none',
          }}
          title="드래그하여 이동"
        >
          <h3 className={styles.title}>조명 자동 그룹핑</h3>
          <button className={styles.closeButton} onClick={onCancel}>
            &times;
          </button>
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {candidates.length === 0 ? (
            <div
              style={{
                padding: '16px',
                background: 'var(--color-bg-hover)',
                borderRadius: 6,
                fontSize: 13,
                color: 'var(--color-text-secondary)',
                textAlign: 'center',
              }}
            >
              그룹화 가능한 조명 셀이 없습니다.
              <br />
              <span style={{ fontSize: 11 }}>
                (layer 가 l-light / l-lightingline 이면서 아직 그룹되지 않은 셀)
              </span>
            </div>
          ) : (
            <>
              {/* 요약 카드 */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr',
                  gap: 8,
                  padding: '12px',
                  background: 'var(--color-bg-hover)',
                  borderRadius: 6,
                }}
              >
                <Stat
                  label="후보"
                  value={candidates.length}
                  unit={`개 (그룹 ${existingGroupIds.size})`}
                />
                <Stat
                  label="신규 그룹"
                  value={stats.newGroupClusters}
                  unit="개"
                  emphasis
                />
                <Stat
                  label="기존 그룹 흡수"
                  value={stats.mergeClusters}
                  unit="건"
                />
                <Stat
                  label="포함 셀"
                  value={totalCellsInClusters}
                  unit={`/ ${candidates.length}`}
                />
              </div>

              {/* 임계값 슬라이더 */}
              <div className={styles.field}>
                <label className={styles.label}>
                  근접 임계값 (도면 좌표 단위){' '}
                  <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                    — 두 조명 거리가 이 값 이하면 같은 그룹
                  </span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input
                    type="range"
                    min={THRESHOLD_MIN}
                    max={THRESHOLD_MAX}
                    step={THRESHOLD_STEP}
                    value={threshold}
                    onChange={(e) =>
                      setThreshold(clampThreshold(parseFloat(e.target.value)))
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    min={THRESHOLD_MIN}
                    max={THRESHOLD_MAX}
                    step={THRESHOLD_STEP}
                    value={Number(threshold.toFixed(3))}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value)
                      if (Number.isFinite(v)) setThreshold(clampThreshold(v))
                    }}
                    className={styles.input}
                    style={{ width: 96 }}
                  />
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  추천 {suggestedThreshold.toFixed(3)} (조명 짧은 변 중앙값{' '}
                  {medianShortSide.toFixed(3)} × 0.5).
                  {orphanCount > 0 && (
                    <>
                      {' · '}
                      {orphanCount}개 조명은 단독이라 그룹에서 제외.
                    </>
                  )}
                </div>
              </div>

              <div
                style={{
                  fontSize: 12,
                  color: 'var(--color-text-secondary)',
                  padding: '8px 12px',
                  background: 'rgba(59, 130, 246, 0.08)',
                  borderRadius: 4,
                  borderLeft: '3px solid var(--color-primary, #3B82F6)',
                }}
              >
                신규 그룹은 자동으로 light-N 번호를 부여받습니다. 임계값을 키우면
                기존 light-N 프레임 근처의 떨어진 라이트도 같은 그룹으로 흡수됩니다.
                결과가 마음에 들지 않으면 Cmd/Ctrl+Z 로 일괄 되돌릴 수 있습니다.
              </div>
            </>
          )}
        </div>

        <div
          className={styles.actions}
          style={{ padding: '0 20px 20px' }}
        >
          <button className={styles.cancelButton} onClick={onCancel}>
            취소
          </button>
          <button
            className={styles.saveButton}
            onClick={() => onConfirm(clusters, threshold)}
            disabled={!canProceed}
            style={{
              opacity: canProceed ? 1 : 0.5,
              cursor: canProceed ? 'pointer' : 'not-allowed',
            }}
          >
            {stats.newGroupClusters > 0 && stats.mergeClusters > 0
              ? `신규 ${stats.newGroupClusters} · 흡수 ${stats.mergeClusters} 적용`
              : stats.mergeClusters > 0
                ? `기존 그룹 ${stats.mergeClusters}건 흡수`
                : `${clusters.length}개 그룹 생성`}
          </button>
        </div>
      </div>
    </div>
  )
}

interface StatProps {
  label: string
  value: number
  unit?: string
  emphasis?: boolean
}

function Stat({ label, value, unit, emphasis }: StatProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{label}</span>
      <span
        style={{
          fontSize: emphasis ? 22 : 18,
          fontWeight: 700,
          color: emphasis ? 'var(--color-primary, #3B82F6)' : 'var(--color-text)',
        }}
      >
        {value}
        {unit && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--color-text-secondary)',
              marginLeft: 2,
            }}
          >
            {unit}
          </span>
        )}
      </span>
    </div>
  )
}
